import { useCallback } from 'react';

type OscillatorKind = OscillatorType;

interface ToneOptions {
  frequency: number;
  duration: number;
  volume?: number;
  type?: OscillatorKind;
}

// One shared AudioContext for the lifetime of the page. Creating a fresh
// context per tone (the previous design) introduced perceptible init latency
// between consecutive tones in a chime — the second tone could drift 20-50ms
// relative to the first, which sounded like "stopped mid-way" or tones
// overlapping unnaturally. Sharing the context gives tight, sample-accurate
// scheduling via a single clock.
let sharedAudioContext: AudioContext | null = null;

/** Testing-only: drop the cached context so the next `playTone` re-creates it. */
export function resetAudioContextForTesting(): void {
  try {
    void sharedAudioContext?.close()?.catch(() => {});
  } catch {
    // Mock contexts in tests may lack `close`; safe to ignore.
  }
  sharedAudioContext = null;
}

function getSharedAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;

  const needsNew = !sharedAudioContext || sharedAudioContext.state === 'closed';
  if (needsNew) {
    const ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!ctor) return null;
    try {
      sharedAudioContext = new ctor();
    } catch {
      return null;
    }
  }
  // Browsers may suspend the context if the tab was backgrounded or no gesture
  // has occurred recently. `resume()` is a no-op when already running.
  if (sharedAudioContext && sharedAudioContext.state === 'suspended') {
    void sharedAudioContext.resume().catch(() => {});
  }
  return sharedAudioContext;
}

/**
 * Play a single tone using Web Audio API. Shared primitive for the variant
 * playbook below.
 */
export function playTone({ frequency, duration, volume = 0.2, type = 'sine' }: ToneOptions) {
  const audioContext = getSharedAudioContext();
  if (!audioContext) return;

  try {
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;

    const now = audioContext.currentTime;
    // Smooth fade in/out to avoid clicks.
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(volume, now + 0.01);
    gainNode.gain.linearRampToValueAtTime(0, now + duration);

    oscillator.start(now);
    oscillator.stop(now + duration);
    // Disconnect once playback is done so the nodes can be GC'd — the context
    // itself stays alive for reuse.
    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  } catch {
    // Ignore audio errors (e.g., user hasn't interacted with page yet)
  }
}

type SoundFn = () => void;

interface Variant {
  label: string;
  /** Fires on click — brief, subtle "we heard you" feedback. */
  start: SoundFn;
  /** Fires when the adapter reaches `running` — "now actually listening". */
  connected: SoundFn;
  /** Fires on stop / cancel. */
  stop: SoundFn;
}

const sineDown: Variant = {
  label: 'Sine sweep (subtle pitch shift) — default',
  start: () => playTone({ frequency: 620, duration: 0.04, volume: 0.08 }),
  connected: () => {
    playTone({ frequency: 520, duration: 0.09, volume: 0.12 });
    setTimeout(() => playTone({ frequency: 780, duration: 0.12, volume: 0.15 }), 70);
  },
  stop: () => {
    playTone({ frequency: 780, duration: 0.09, volume: 0.12 });
    setTimeout(() => playTone({ frequency: 520, duration: 0.12, volume: 0.15 }), 70);
  },
};

const chime: Variant = {
  label: 'Chime (ascending/descending triad)',
  start: () => playTone({ frequency: 440, duration: 0.04, volume: 0.08, type: 'triangle' }),
  connected: () => {
    playTone({ frequency: 523, duration: 0.12, volume: 0.15 }); // C5
    setTimeout(() => playTone({ frequency: 659, duration: 0.12, volume: 0.15 }), 80); // E5
    setTimeout(() => playTone({ frequency: 784, duration: 0.15, volume: 0.2 }), 160); // G5
  },
  stop: () => {
    playTone({ frequency: 784, duration: 0.12, volume: 0.15 }); // G5
    setTimeout(() => playTone({ frequency: 659, duration: 0.12, volume: 0.15 }), 80); // E5
    setTimeout(() => playTone({ frequency: 523, duration: 0.15, volume: 0.2 }), 160); // C5
  },
};

const arc: Variant = {
  label: 'Arc (three-tone glide)',
  start: () => playTone({ frequency: 500, duration: 0.04, volume: 0.08 }),
  connected: () => {
    playTone({ frequency: 440, duration: 0.06, volume: 0.12 });
    setTimeout(() => playTone({ frequency: 587, duration: 0.06, volume: 0.13 }), 45);
    setTimeout(() => playTone({ frequency: 740, duration: 0.08, volume: 0.15 }), 90);
  },
  stop: () => {
    playTone({ frequency: 740, duration: 0.06, volume: 0.12 });
    setTimeout(() => playTone({ frequency: 587, duration: 0.06, volume: 0.13 }), 45);
    setTimeout(() => playTone({ frequency: 440, duration: 0.08, volume: 0.15 }), 90);
  },
};

const siri: Variant = {
  label: 'Siri-like (two-tone rise/fall)',
  start: () => playTone({ frequency: 880, duration: 0.035, volume: 0.08 }),
  connected: () => {
    playTone({ frequency: 740, duration: 0.08, volume: 0.12 }); // F#5
    setTimeout(() => playTone({ frequency: 988, duration: 0.1, volume: 0.16 }), 75); // B5
  },
  stop: () => {
    playTone({ frequency: 988, duration: 0.08, volume: 0.12 });
    setTimeout(() => playTone({ frequency: 740, duration: 0.1, volume: 0.16 }), 75);
  },
};

export const DICTATION_SOUND_VARIANTS = {
  sineDown,
  chime,
  arc,
  siri,
} as const;

export type DictationSoundVariant = keyof typeof DICTATION_SOUND_VARIANTS;

/**
 * Production default. Tenants can override via the `meta.dictationSoundVariant`
 * config field — see `useDictationHandlers`.
 */
export const DEFAULT_DICTATION_SOUND_VARIANT: DictationSoundVariant = 'sineDown';

/**
 * Hook to get memoized dictation sound functions for the given variant.
 * Only plays sounds when enabled. Defaults to the production variant.
 * Unknown variant strings (e.g. from runtime config drift) fall back to the
 * default rather than throwing on `DICTATION_SOUND_VARIANTS[variant]`.
 */
export function useDictationSounds(
  enableSounds: boolean,
  variant: DictationSoundVariant = DEFAULT_DICTATION_SOUND_VARIANT,
) {
  const safeVariant: DictationSoundVariant =
    variant in DICTATION_SOUND_VARIANTS ? variant : DEFAULT_DICTATION_SOUND_VARIANT;

  const playStartSound = useCallback(() => {
    if (enableSounds) DICTATION_SOUND_VARIANTS[safeVariant].start();
  }, [enableSounds, safeVariant]);

  const playConnectedSound = useCallback(() => {
    if (enableSounds) DICTATION_SOUND_VARIANTS[safeVariant].connected();
  }, [enableSounds, safeVariant]);

  const playStopSound = useCallback(() => {
    if (enableSounds) DICTATION_SOUND_VARIANTS[safeVariant].stop();
  }, [enableSounds, safeVariant]);

  return { playStartSound, playConnectedSound, playStopSound };
}
